/**
 * Data contracts for Easy SaaS Tracker.
 *
 * Field names match the original application's data (and its JSON backup format), so an
 * existing backup can be imported unchanged and a backend can store the same shapes.
 * Dates are ISO calendar dates `YYYY-MM-DD` (no time, no time zone).
 */

export type ISODate = string;

export const PROJECT_STATUSES = ['Live', 'In Progress', 'Planning', 'On Hold', 'Delayed', 'Completed'] as const;
export type ProjectStatus = (typeof PROJECT_STATUSES)[number];

/**
 * How a metric's number is read. Set per project in Configure metrics; never guessed.
 * - `asof`   "As of day": count at the end of that day (compare with the previous day)
 * - `daily`  "That day only": happened on that day (add days to get a total)
 * - `todate` "To date": running total since the start
 */
export type MetricKind = 'asof' | 'daily' | 'todate';

export interface ProjectLink {
  label: string;
  url: string;
}

/** One "Change Deadline" entry. Setting the first deadline is not a change. */
export interface DeadlineExtension {
  /** Date the change was made */
  on: ISODate;
  /** Deadline before the change */
  from: ISODate;
  /** New deadline */
  to: ISODate;
  /** Mandatory reason (at least 10 characters) */
  reason: string;
}

export interface Project {
  id: string;
  name: string;
  /** What the project delivers */
  service: string;
  /** Lead / Owner — free text, as in the original app (not a member id) */
  lead: string;
  status: ProjectStatus;
  /** Original deadline (target completion date) */
  planned: ISODate | '';
  /** Current deadline after changes; empty when never changed */
  revised: ISODate | '';
  /** Reason given when the first deadline was set */
  deadlineBasis?: string;
  /** Reason of the latest deadline change */
  delayReason: string;
  /** What is done (free text) */
  completed: string;
  /** What is left (free text) */
  pending: string;
  extensions: DeadlineExtension[];
  links: ProjectLink[];
  /** Member ids assigned to the project */
  members: string[];
  /** Metric names configured for this project, in display order */
  metricKeys: string[];
  /** Type of each configured metric; a label with no entry has no type set */
  metricKinds: Record<string, MetricKind>;
}

/** A member ↔ project assignment (derived from Project.members). */
export interface ProjectMember {
  projectId: string;
  memberId: string;
}

export interface Member {
  id: string;
  name: string;
  /** Role / Team, free text (e.g. Developer, Support Team) */
  role: string;
}

/** "Employee Log Work": who worked how long on what, on one day. */
export interface WorkLog {
  id: string;
  date: ISODate;
  /** Empty string = General work (no project) */
  projectId: string;
  memberId: string;
  hours: number;
  work: string;
}

/** A statistic recorded in a Project Log. Value kept as entered, e.g. "1,443", "25%", "25m 59s". */
export interface ProjectMetric {
  label: string;
  value: string;
}

/** "Project Log Work": the project's own update and statistics for one day. One per project per day. */
export interface ProjectUpdate {
  id: string;
  date: ISODate;
  projectId: string;
  note: string;
  metrics: ProjectMetric[];
}

export interface Settings {
  /** Daily report title; `{date}` is replaced by e.g. "25 September" */
  reportTitle: string;
  /** Member ids listed in the report's "Management" line */
  generalMembers: string[];
}

/** Whole-dataset backup, identical in shape to the original app's JSON backup. */
export interface Backup {
  projects: Project[];
  members: Member[];
  logs: WorkLog[];
  updates: ProjectUpdate[];
  settings: Settings;
}

/* ---------- inputs ---------- */

export type ProjectInput = Pick<Project, 'name' | 'service' | 'lead' | 'status' | 'completed' | 'pending'> & {
  planned?: ISODate | '';
};
export type ProjectPatch = Partial<Omit<Project, 'id'>>;
export type MemberInput = Pick<Member, 'name' | 'role'>;
export type WorkLogInput = Omit<WorkLog, 'id'>;
export type ProjectUpdateInput = Omit<ProjectUpdate, 'id'>;
export interface DeadlineChangeInput {
  /** New deadline; must be after the current one when a deadline exists */
  to: ISODate;
  /** At least 10 characters */
  reason: string;
}

export interface WorkLogFilter {
  from?: ISODate;
  to?: ISODate;
  projectId?: string;
  memberId?: string;
}
export interface ProjectUpdateFilter {
  from?: ISODate;
  to?: ISODate;
  projectId?: string;
}

/* ---------- users & permissions (filled by the backend's auth in production) ---------- */

export type Role = 'admin' | 'management' | 'manager' | 'employee';
export type Permission =
  | 'management:view'
  | 'projects:write'
  | 'members:write'
  | 'worklog:write'
  | 'updates:write'
  | 'settings:write'
  | 'backup:manage';

export interface User {
  id: string;
  name: string;
  email?: string;
  roles: Role[];
  permissions: Permission[];
}

/* ---------- Daily Update .docx import ---------- */

export interface ParsedDayBucket {
  logs: { name: string; hours: number; work: string }[];
  metrics: ProjectMetric[];
  notes: string[];
}
export interface ParsedDay {
  date: ISODate;
  general: ParsedDayBucket;
  projects: Record<string, ParsedDayBucket>;
}
export interface ImportSummary {
  days: number;
  workLogs: number;
  projectLogs: number;
  newMembers: string[];
  newProjects: string[];
}
